import { isBlank } from "@freightsimple/shared/Helpers/isNotBlank";
import { ArrowRightIcon } from "@phosphor-icons/react/ArrowRight";
import { Tooltip } from "@freightsimple/ui-core/Feedback/Tooltip";

// Every line fills its column and ellipsises inside it. A line sized to its own
// text instead runs out of the block: the shipment row sizes this to a
// percentage of the row, so a long business name spilled sideways and landed
// under the carrier logo in the next column.
//
// `stretch` on the tooltips is part of the same thing — the tooltip's trigger
// wrapper becomes the flex item, so without it the line's `w-full` resolves
// against a shrink-wrapped span and never truncates.
const lineClasses = "w-full overflow-hidden text-ellipsis whitespace-nowrap";

function CompanyComponent_({
  businessName,
  compact,
}: {
  businessName: string;
  compact: boolean;
}) {
  // Only the name tightens in compact, not the whole column: the city and the
  // reference keep the room they had. Payments and the receipt dialog are the
  // compact call sites, and a city has no tooltip to recover from an ellipsis.
  const line = (
    <div
      className={`${lineClasses} text-[12px] font-medium text-foreground ${
        compact ? "max-w-[140px]" : ""
      }`}
    >
      {businessName}
    </div>
  );

  if (!compact) {
    return line;
  }

  return (
    <Tooltip side="right" content={businessName} stretch>
      {line}
    </Tooltip>
  );
}

function City_({ children }: { children: string }) {
  return (
    <div
      className={`${lineClasses} text-[12px] font-medium text-content-muted`}
    >
      {children}
    </div>
  );
}

function ReferenceComponent_({ reference }: { reference: string }) {
  const line = (
    <div className={`${lineClasses} text-[11px] font-light text-content-muted`}>
      {reference}
    </div>
  );

  if (reference.length <= 20) {
    return line;
  }

  return (
    <Tooltip side="right" content={reference} stretch>
      {line}
    </Tooltip>
  );
}

function LocationDescription_({
  align,
  businessName,
  city,
  reference,
  compact,
}: {
  align: "left" | "right";
  businessName: string;
  city: string;
  reference: string;
  compact: boolean;
}) {
  const isBusinessNameBlank = isBlank(businessName);
  const isCityBlank = isBlank(city);
  const isReferenceBlank = isBlank(reference);

  return (
    // The right-aligned (pickup) side flips to left-aligned on a narrow row,
    // where the from→to pair reads top-down instead of meeting at the arrow.
    // min-w-0 is what lets the column give way when the row is tight, so the
    // lines truncate inside it rather than overrunning the column beside them.
    <div
      className={`flex max-w-[196px] min-w-0 flex-col flex-nowrap ${
        align === "right"
          ? "text-right in-[.dashboard-row-narrow]:text-left"
          : "text-left"
      }`}
    >
      {isBusinessNameBlank && isCityBlank && <City_>{"---"}</City_>}
      {!isBusinessNameBlank && (
        <CompanyComponent_ businessName={businessName} compact={compact} />
      )}
      {!isCityBlank && <City_>{city}</City_>}
      {!isReferenceBlank && <ReferenceComponent_ reference={reference} />}
    </div>
  );
}

interface SmallFromToProps {
  pickupBusinessName: string;
  pickupCity: string;
  pickupReference: string;
  deliveryBusinessName: string;
  deliveryCity: string;
  deliveryReference: string;
  compact?: boolean | undefined;
}

function ShipmentSmallFromTo(props: SmallFromToProps) {
  const {
    pickupBusinessName,
    pickupCity,
    pickupReference,
    deliveryBusinessName,
    deliveryCity,
    deliveryReference,
    compact = false,
  } = props;

  return (
    // On a narrow row it takes the full width on its own line.
    <div className="flex w-[36%] min-w-[280px] flex-row items-center in-[.dashboard-row-narrow]:w-full in-[.dashboard-row-narrow]:min-w-0">
      <LocationDescription_
        align="right"
        businessName={pickupBusinessName}
        city={pickupCity}
        reference={pickupReference}
        compact={compact}
      />
      <ArrowRightIcon
        className="mx-4 h-5 w-5 shrink-0 text-foreground"
        weight="fill"
      />
      <LocationDescription_
        align="left"
        businessName={deliveryBusinessName}
        city={deliveryCity}
        reference={deliveryReference}
        compact={compact}
      />
    </div>
  );
}
export default ShipmentSmallFromTo;
